import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import * as XLSX from 'xlsx';
import Layout from '../../components/layout/Layout';
import PageCard from '../../components/ui/PageCard';
import MagazinePageHeader from './MagazinePageHeader';
import {
  getIssueBookingReport,
  getIssues,
  getPublications,
  IssueBookingReport,
} from '../../api/magazineService';

const formatMoney = (amount?: number | string | null, currency = 'GBP') => {
  const n = Number(amount);
  if (Number.isNaN(n)) return '—';
  const code = currency.trim().toUpperCase() || 'GBP';
  try {
    return new Intl.NumberFormat('en-GB', { style: 'currency', currency: code }).format(n);
  } catch {
    return `${code} ${n.toFixed(2)}`;
  }
};

const formatGBPMoney = (
  amount?: number | string | null,
  rate?: number
) => {
  const value = Number(amount);
  const exchangeRate = Number(rate);

  if (
    amount == null ||
    rate == null ||
    exchangeRate === 0 ||
    !Number.isFinite(value) ||
    !Number.isFinite(exchangeRate)
  ) {
    return '';
  }

  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
  }).format(value * exchangeRate);
};

const bookingCurrency = (row: IssueBookingReport['bookings'][number]) =>
  row.proposal_currency || 'GBP';

const MagazineReports = () => {
  const [searchParams] = useSearchParams();
  const [publications, setPublications] = useState<any[]>([]);
  const [issues, setIssues] = useState<any[]>([]);
  const [publicationId, setPublicationId] = useState('');
  const [issueId, setIssueId] = useState(searchParams.get('issue_id') || '');
  const [statusFilter, setStatusFilter] = useState('');
  const [report, setReport] = useState<IssueBookingReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    getPublications().then(setPublications);
  }, []);

  useEffect(() => {
    getIssues(publicationId || undefined).then((data) => {
      setIssues(data);
      if (issueId && !data.some((i: any) => String(i.id) === issueId)) {
        setIssueId('');
        setReport(null);
      }
    });
  }, [publicationId]);

  const loadReport = async () => {
    if (!issueId) return;
    setLoading(true);
    setError(null);
    const data = await getIssueBookingReport(issueId, statusFilter || undefined);
    if (!data) {
      setError('Could not load report for this issue.');
      setReport(null);
    } else {
      setReport(data);
    }
    setLoading(false);
  };

  const exportBookedAds = () => {
    if (!report || report.bookings.length === 0) return;

    const rows = report.bookings.map((row) => ({
      Publication: report.issue.publication?.name || '',
      Issue: report.issue.title,
      'Issue date': report.issue.issue_date || '',
      Company: row.company?.name || '',
      Advert: row.ad_slot.label,
      Size: row.ad_slot.size || '',
      Price: Number(row.booked_price),
      Currency: bookingCurrency(row).toUpperCase(),
      Status: row.proposal_status || '',
      'Sales representative': row.sales_rep?.username || row.sales_rep?.email || '',
      'Proposal ID': row.proposal_id || '',
      Source: row.source,
      Link: row.href || '',
    }));

    const worksheet = XLSX.utils.json_to_sheet(rows);
    worksheet['!autofilter'] = { ref: worksheet['!ref'] || 'A1:M1' };
    worksheet['!cols'] = [
      { wch: 24 }, { wch: 24 }, { wch: 14 }, { wch: 28 },
      { wch: 24 }, { wch: 14 }, { wch: 14 }, { wch: 12 },
      { wch: 12 }, { wch: 24 }, { wch: 14 }, { wch: 14 }, { wch: 30 },
    ];

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Booked Ads');
    const issueName = `${report.issue.publication?.name || 'publication'}-${report.issue.title}`
      .replace(/[^a-z0-9]+/gi, '-')
      .replace(/^-|-$/g, '')
      .toLowerCase();
    XLSX.writeFile(workbook, `${issueName || 'issue'}-booked-ads.xlsx`);
  };

  useEffect(() => {
    if (issueId) loadReport();
  }, [issueId, statusFilter]);

  const revenueByCurrency = (report?.bookings || []).reduce<Record<string, number>>((totals, row) => {
    const currency = bookingCurrency(row).toUpperCase();
    totals[currency] = (totals[currency] || 0) + Number(row.booked_price || 0);
    return totals;
  }, {});

  return (
    <Layout>
      <MagazinePageHeader
        title="Magazine reports"
        description="Per-issue report of booked adverts (sent and signed proposals)."
      />

      <PageCard className="mb-6">
        <div className="flex flex-wrap items-end gap-3">
          <label className="text-sm text-gray-600">
            Publication
            <select
              className="ml-2 max-w-[200px] rounded border border-gray-300 px-2 py-1.5 text-sm"
              value={publicationId}
              onChange={(e) => setPublicationId(e.target.value)}
            >
              <option value="">All</option>
              {publications.map((p) => (
                <option key={p.id} value={String(p.id)}>{p.name}</option>
              ))}
            </select>
          </label>
          <label className="text-sm text-gray-600">
            Issue
            <select
              className="ml-2 max-w-[240px] rounded border border-gray-300 px-2 py-1.5 text-sm"
              value={issueId}
              onChange={(e) => setIssueId(e.target.value)}
            >
              <option value="">Select issue</option>
              {issues.map((i) => (
                <option key={i.id} value={String(i.id)}>
                  {i.publication?.name ? `${i.publication.name} — ` : ''}{i.title}
                </option>
              ))}
            </select>
          </label>
          <label className="text-sm text-gray-600">
            Status
            <select
              className="ml-2 rounded border border-gray-300 px-2 py-1.5 text-sm"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              disabled={!issueId}
            >
              <option value="">Sent + signed</option>
              <option value="sent">Sent only</option>
              <option value="signed">Signed only</option>
            </select>
          </label>
          <button
            type="button"
            onClick={loadReport}
            disabled={!issueId || loading}
            className="rounded bg-torch-primary px-3 py-1.5 text-sm font-medium text-white hover:bg-torch-primary-dark disabled:opacity-50"
          >
            Refresh
          </button>
          <button
            type="button"
            onClick={exportBookedAds}
            disabled={loading || !report?.bookings.length}
            className="rounded border border-torch-primary px-3 py-1.5 text-sm font-medium text-torch-primary hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Export booked ads
          </button>
        </div>
      </PageCard>

      {!issueId && (
        <PageCard>
          <p className="text-sm text-gray-500">Select an issue to view its booking report.</p>
        </PageCard>
      )}

      {issueId && loading && (
        <PageCard><p className="text-sm text-gray-500">Loading report…</p></PageCard>
      )}

      {issueId && !loading && error && (
        <PageCard><p className="text-sm text-red-600">{error}</p></PageCard>
      )}

      {issueId && !loading && report && (
        <div className="space-y-6">
          <PageCard>
            <h2 className="mb-1 text-lg font-semibold text-gray-900">
              {report.issue.publication?.name || 'Publication'} — {report.issue.title}
            </h2>
            <p className="mb-4 text-sm text-gray-500">
              Issue date: {report.issue.issue_date || '—'} · Status: {report.issue.status}
            </p>
            <div className="grid gap-4 sm:grid-cols-3">
              <div>
                <div className="text-xs font-medium uppercase text-gray-500">Ad slots</div>
                <div className="mt-1 text-2xl font-semibold text-gray-900">{report.summary.slot_count}</div>
              </div>
              <div>
                <div className="text-xs font-medium uppercase text-gray-500">Bookings</div>
                <div className="mt-1 text-2xl font-semibold text-gray-900">{report.summary.booking_count}</div>
              </div>
              <div>
                <div className="text-xs font-medium uppercase text-gray-500">Booked revenue</div>
                <div className="mt-1 text-2xl font-semibold text-gray-900">
                  {Object.entries(revenueByCurrency).map(([currency, total]) => (
                    <div key={currency}>{formatMoney(total, currency)}</div>
                  ))}
                </div>
              </div>
            </div>
          </PageCard>

          <PageCard className="overflow-x-auto">
            <h3 className="mb-4 text-sm font-semibold text-gray-900">Booked adverts</h3>
            {report.bookings.length === 0 ? (
              <p className="text-sm text-gray-500">No bookings for this issue with the selected status.</p>
            ) : (
              <table className="min-w-full text-left text-sm">
                <thead>
                  <tr className="border-b border-gray-200 text-gray-500">
                    <th className="py-2 pr-4">Company</th>
                    <th className="py-2 pr-4">Advert</th>
                    <th className="py-2 pr-4">Size</th>
                    <th className="py-2 pr-4">Price</th>
                    <th className="py-2 pr-4">Price(GBP)</th>
                    <th className="py-2 pr-4">Status</th>
                    <th className="py-2 pr-4">Sales rep</th>
                    <th className="py-2">Link</th>
                  </tr>
                </thead>
                <tbody>
                  {report.bookings.map((row, idx) => (
                    <tr key={`${row.source}-${row.proposal_id}-${row.ad_slot.id}-${idx}`} className="border-b border-gray-100">
                      <td className="py-3 pr-4 font-medium text-gray-900">{row.company?.name || '—'}</td>
                      <td className="py-3 pr-4 text-gray-900">{row.ad_slot.label}</td>
                      <td className="py-3 pr-4 text-gray-600">{row.ad_slot.size || '—'}</td>
                      <td className="py-3 pr-4 text-gray-900">
                        {formatMoney(row.booked_price, bookingCurrency(row))}
                      </td>
                       <td className="py-3 pr-4 text-gray-900">
                        {formatGBPMoney(row.booked_price, row.proposal_gbp_rate)}
                       </td>
                      <td className="py-3 pr-4 capitalize text-gray-600">{row.proposal_status || '—'}</td>
                      <td className="py-3 pr-4 text-gray-600">
                        {row.sales_rep?.username || row.sales_rep?.email || '—'}
                      </td>
                      <td className="py-3">
                        {row.href ? (
                          <Link to={row.href} className="text-sm text-torch-primary hover:underline">
                            Proposal
                          </Link>
                        ) : (
                          '—'
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </PageCard>

          <PageCard className="overflow-x-auto">
            <h3 className="mb-4 text-sm font-semibold text-gray-900">Inventory snapshot</h3>
            {report.slots.length === 0 ? (
              <p className="text-sm text-gray-500">No ad slots on this issue.</p>
            ) : (
              <table className="min-w-full text-left text-sm">
                <thead>
                  <tr className="border-b border-gray-200 text-gray-500">
                    <th className="py-2 pr-4">Label</th>
                    <th className="py-2 pr-4">Size</th>
                    {/* <th className="py-2 pr-4">Rate card</th> */}
                    <th className="py-2 pr-4">Available</th>
                    <th className="py-2">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {report.slots.map((slot) => (
                    <tr key={slot.id} className="border-b border-gray-100">
                      <td className="py-3 pr-4 font-medium text-gray-900">{slot.label}</td>
                      <td className="py-3 pr-4 text-gray-600">{slot.size || '—'}</td>
                      {/* <td className="py-3 pr-4 text-gray-900">{formatMoney(slot.price)}</td> */}
                      {/* <td className="py-3 pr-4 text-gray-600">
                        {slot.available} / {slot.quantity}
                      </td> */}
                      <td className="py-3 capitalize text-gray-600">{slot.status}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </PageCard>
        </div>
      )}
    </Layout>
  );
};

export default MagazineReports;
